import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useCurrency } from '../contexts/CurrencyContext'
import { shootService } from '../services/shootService'
import { clientService } from '../services/clientService'
import { invoiceService } from '../services/invoiceService'
import { documentService } from '../services/documentService'
import { Shoot, Client } from '../types'
import StatsModal from '../components/StatsModal'
import PremiumFeaturesShowcase from '../components/PremiumFeaturesShowcase'
import ProfileDropdown from '../components/ProfileDropdown'
import NotificationDropdown from '../components/NotificationDropdown'
import SettingsDropdown from '../components/SettingsDropdown'
import AppHeader from '../components/AppHeader'
import MiniCalendarWidget from '../components/MiniCalendarWidget'
import Footer from '../components/Footer'
import ShootDetailsModal from '../components/ShootDetailsModal'
import { 
  CameraIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  PlusIcon,
  CalendarIcon,
  ChartBarIcon,
  CogIcon,
  SparklesIcon,
  SwatchIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline'
import AIBusinessAssistant from '../components/AIBusinessAssistant'

const DashboardPage = () => {
  const { user } = useAuth()
  const { selectedCurrency } = useCurrency()
  const navigate = useNavigate()
  
  const [allShoots, setAllShoots] = useState<Shoot[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [monthlyIncome, setMonthlyIncome] = useState<number>(0)
  const [totalInvoices, setTotalInvoices] = useState<number>(0)
  const [totalDocuments, setTotalDocuments] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [statsModal, setStatsModal] = useState<{
    isOpen: boolean
    type: 'clients' | 'shoots' | 'upcoming' | 'revenue'
  }>({ isOpen: false, type: 'clients' })
  const [revenueRefreshKey, setRevenueRefreshKey] = useState(0)
  const [showPremiumFeatures, setShowPremiumFeatures] = useState(false)
  const [showAIAssistant, setShowAIAssistant] = useState(false)
  
  // Shoot details modal state
  const [shootDetailsModal, setShootDetailsModal] = useState<{
    isOpen: boolean
    shoot: Shoot | null
  }>({ isOpen: false, shoot: null })
  const [savingShootDetails, setSavingShootDetails] = useState(false)

  // Load shoots data
  useEffect(() => {
    if (user?.id) {
      loadShootsData()
    }
  }, [user?.id])

  // Listen for invoice status changes to refresh monthly income
  useEffect(() => {
    const handleInvoiceStatusChange = async (event: Event) => {
      const customEvent = event as CustomEvent
      const { newStatus, invoiceId, amount } = customEvent.detail
      console.log('Dashboard received invoice status change:', { newStatus, invoiceId, amount })
      
      if (newStatus === 'paid' && user?.id) {
        console.log('Refreshing monthly income for user:', user.id)
        // Refresh monthly income when an invoice is marked as paid
        const incomeResult = await invoiceService.getMonthlyIncome(user.id)
        console.log('Monthly income result:', incomeResult)
        
        if (incomeResult.success) {
          console.log('Updating monthly income from', monthlyIncome, 'to', incomeResult.income)
          setMonthlyIncome(incomeResult.income || 0)
          // Trigger revenue analytics refresh
          setRevenueRefreshKey(prev => prev + 1)
        } else {
          console.error('Failed to get monthly income:', incomeResult.error)
        }
      }
    }

    console.log('Setting up invoice status change listener')
    window.addEventListener('invoiceStatusChanged', handleInvoiceStatusChange)
    
    return () => {
      console.log('Removing invoice status change listener')
      window.removeEventListener('invoiceStatusChanged', handleInvoiceStatusChange)
    }
  }, [user?.id, monthlyIncome])

  const loadShootsData = async () => {
    try {
      setLoading(true)
      const [all, clientsData, invoicesResult, incomeResult, documentsResult] = await Promise.all([
        shootService.getShootsForUser(user!.id),
        clientService.getClientsForUser(user!.id),
        invoiceService.getInvoices(user!.id),
        invoiceService.getMonthlyIncome(user!.id),
        documentService.getDocumentStats(user!.id)
      ])
      setAllShoots(all)
      setClients(clientsData)
      setTotalInvoices(invoicesResult.success ? invoicesResult.invoices?.length || 0 : 0)
      setMonthlyIncome(incomeResult.success ? incomeResult.income || 0 : 0)
      setTotalDocuments(documentsResult.success ? documentsResult.stats?.totalDocuments || 0 : 0)
    } catch (err) {
      console.error('Error loading data:', err)
    } finally {
      setLoading(false)
    }
  }

  const quickActions = [
    {
      title: 'New Shoot',
      description: 'Schedule a new photo or video shoot',
      icon: CameraIcon,
      color: 'from-blue-500 to-purple-600',
      action: () => navigate('/new-shoot')
    },
    {
      title: 'Add Client',
      description: 'Create a new client profile',
      icon: UserGroupIcon,
      color: 'from-emerald-500 to-teal-600',
      action: () => navigate('/add-client')
    },
    {
      title: 'Create Invoice',
      description: 'Generate a new invoice',
      icon: DocumentTextIcon,
      color: 'from-orange-500 to-red-600',
      action: () => navigate('/invoices/new')
    },
    {
      title: 'Task List',
      description: 'Manage your tasks and deadlines',
      icon: ClipboardDocumentListIcon,
      color: 'from-purple-500 to-pink-600',
      action: () => navigate('/tasks')
    },
    {
      title: 'Client Portals',
      description: 'Access your clients\' portals',
      icon: UserGroupIcon,
      color: 'from-indigo-500 to-blue-600',
      action: () => navigate('/client-portals')
    }
  ]

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedCurrency.code
    }).format(amount)
  }

  const isPremiumUser = user?.subscriptionStatus === 'professional' || user?.subscriptionStatus === 'studio'

  // Debug subscription status (temporary)
  console.log('User subscription status:', user?.subscriptionStatus)
  console.log('Is premium user:', isPremiumUser)

  // Calculate shoot statistics
  const scheduledShoots = allShoots.filter(shoot => shoot.status === 'scheduled')
  const completedShoots = allShoots.filter(shoot => shoot.status === 'completed')

  const stats = [
    { label: 'Total Clients', value: clients.length.toString(), icon: UserGroupIcon, type: 'clients' as const },
    { label: 'Scheduled Shoots', value: scheduledShoots.length.toString(), icon: CalendarIcon, type: 'upcoming' as const },
    { label: 'Completed Shoots', value: completedShoots.length.toString(), icon: CameraIcon, type: 'shoots' as const },
    { 
      label: 'This Month Income', 
      value: formatCurrency(monthlyIncome), 
      icon: ChartBarIcon, 
      type: 'revenue' as const,
      isPremiumOnly: true,
      isBlurred: !isPremiumUser
    }
  ]

  // For now, show empty activity - this will be replaced with real activity tracking later
  const recentActivity: { action: string; client: string; time: string }[] = []

  const handleEditShoot = (shoot: Shoot) => {
    // Navigate to edit shoot page (we'll use the new shoot page with pre-filled data)
    navigate('/new-shoot', { state: { editShoot: shoot } })
  }

  const handleDeleteShoot = async (shootId: string) => {
    try {
      await shootService.deleteShoot(shootId)
      // Refresh the data
      await loadShootsData()
      setStatsModal({ isOpen: false, type: 'upcoming' })
    } catch (error) {
      console.error('Error deleting shoot:', error)
      alert('Failed to delete shoot. Please try again.')
    }
  }

  // Handle opening shoot details modal
  const handleOpenShootDetails = (shoot: Shoot) => {
    setShootDetailsModal({ isOpen: true, shoot })
  }

  // Handle saving shoot details
  const handleSaveShootDetails = async (shootId: string, updates: Partial<Shoot>) => {
    try {
      setSavingShootDetails(true)
      await shootService.updateShootDetails(shootId, updates)
      // Refresh the shoots data
      await loadShootsData()
      // Close the modal
      setShootDetailsModal({ isOpen: false, shoot: null })
    } catch (error) {
      console.error('Error saving shoot details:', error)
      alert('Failed to save shoot details. Please try again.')
    } finally {
      setSavingShootDetails(false)
    }
  }

  // Handle closing shoot details modal
  const handleCloseShootDetails = () => {
    setShootDetailsModal({ isOpen: false, shoot: null })
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader>
        <NotificationDropdown />
        <SettingsDropdown />
        <ProfileDropdown />
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 sm:mb-12"
        >
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-4">
            Welcome back, <span className="text-gradient">{user?.firstName}</span>!
          </h1>
          <p className="text-gray-400 text-base sm:text-lg">
            Here's what's happening with your creative business today.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-8 sm:mb-12"
        >
          {stats.map((stat, index) => (
            <motion.button
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              onClick={() => stat.isPremiumOnly && !isPremiumUser ? navigate('/upgrade') : setStatsModal({ isOpen: true, type: stat.type })}
              className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:scale-105 transition-transform text-left cursor-pointer relative"
              whileHover={{ y: -2 }}
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <stat.icon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-300 flex-shrink-0" />
                <span className={`text-lg sm:text-2xl lg:text-3xl font-bold text-white ${stat.isBlurred ? 'blur-sm' : ''}`}>
                  {stat.value}
                </span>
              </div>
              <p className="text-gray-400 font-medium text-xs sm:text-sm lg:text-base leading-tight">{stat.label}</p>
              
              {/* Premium Unlock Overlay */}
              {stat.isPremiumOnly && !isPremiumUser && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl sm:rounded-2xl backdrop-blur-sm">
                  <div className="text-center px-2">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-1 sm:mb-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-black text-xs sm:text-sm font-bold">🔒</span>
                    </div>
                    <p className="text-white text-xs sm:text-sm font-medium">Upgrade to Pro</p>
                    <p className="text-gray-300 text-xs">to unlock</p>
                  </div>
                </div>
              )}
            </motion.button>
          ))}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8 sm:mb-12"
        >
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {quickActions.map((action, index) => (
              <motion.button
                key={action.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.05 }}
                onClick={action.action}
                className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:scale-105 transition-all group"
                whileHover={{ y: -5 }}
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r ${action.color} flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform`}>
                  <action.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2 text-sm sm:text-base">{action.title}</h3>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">{action.description}</p>
                <div className="flex items-center mt-3 sm:mt-4 text-blue-400 group-hover:text-blue-300 transition-colors">
                  <span className="text-xs sm:text-sm font-medium">Get started</span>
                  <PlusIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Premium Features Status */}
        {!isPremiumUser ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.24 }}
            className="mb-6 sm:mb-8"
          >
            <motion.button
              onClick={() => navigate('/upgrade')}
              className="w-full glass-mockup rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:scale-[1.02] transition-all group border-2 border-dashed border-yellow-400/30 hover:border-yellow-400/50"
              whileHover={{ y: -2 }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg sm:text-xl">✨</span>
                  </div>
                  <div className="text-left min-w-0 flex-1">
                    <h3 className="text-white font-semibold text-sm sm:text-base">Unlock Premium Features</h3>
                    <p className="text-gray-400 text-xs sm:text-sm">AI automation, analytics, team tools & more</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <div className="text-white font-bold text-sm sm:text-base">Starting at $29/mo</div>
                  <div className="text-gray-400 text-xs">30-day guarantee</div>
                </div>
              </div>
            </motion.button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.24 }}
            className="mb-6 sm:mb-8"
          >
            <motion.button
              onClick={() => setShowPremiumFeatures(true)}
              className="w-full glass-mockup rounded-xl sm:rounded-2xl p-3 sm:p-4 border-2 border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 transition-all group"
              whileHover={{ y: -2 }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r from-emerald-400 to-green-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg sm:text-xl">🎉</span>
                  </div>
                  <div className="text-left min-w-0 flex-1">
                    <h3 className="text-white font-semibold text-sm sm:text-base">Professional Plan Active</h3>
                    <p className="text-emerald-400 text-xs sm:text-sm">All premium features unlocked • Click to explore</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <div className="text-emerald-400 font-bold text-sm sm:text-base">✓ Premium</div>
                  <div className="text-emerald-300 text-xs group-hover:text-emerald-200">View Features →</div>
                </div>
              </div>
            </motion.button>
          </motion.div>
        )}

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-8 sm:mb-12"
        >
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white">Quick Links</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/clients')}
              className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:bg-white/10 transition-all group"
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <UserGroupIcon className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-400 flex-shrink-0" />
                <span className="text-xl sm:text-2xl font-bold text-white">{clients.length}</span>
              </div>
              <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">View All Clients</h3>
              <p className="text-gray-400 text-xs sm:text-sm">Manage your client database</p>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/calendar')}
              className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:bg-white/10 transition-all group"
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <CalendarIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400 flex-shrink-0" />
                <span className="text-xl sm:text-2xl font-bold text-white">{allShoots.length}</span>
              </div>
              <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Full Calendar</h3>
              <p className="text-gray-400 text-xs sm:text-sm">View all scheduled shoots</p>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/invoices')}
              className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:bg-white/10 transition-all group"
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <DocumentTextIcon className="w-6 h-6 sm:w-8 sm:h-8 text-orange-400 flex-shrink-0" />
                <span className="text-xl sm:text-2xl font-bold text-white">{totalInvoices}</span>
              </div>
              <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">View All Invoices</h3>
              <p className="text-gray-400 text-xs sm:text-sm">Manage your invoices</p>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/documents')}
              className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:bg-white/10 transition-all group"
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <DocumentDuplicateIcon className="w-6 h-6 sm:w-8 sm:h-8 text-cyan-400 flex-shrink-0" />
                <span className="text-xl sm:text-2xl font-bold text-white">{totalDocuments}</span>
              </div>
              <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Documents & Contracts</h3>
              <p className="text-gray-400 text-xs sm:text-sm">Manage business documents</p>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/tasks')}
              className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:bg-white/10 transition-all group sm:col-span-2 lg:col-span-1"
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <ClipboardDocumentListIcon className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400 flex-shrink-0" />
                <span className="text-xl sm:text-2xl font-bold text-white">0</span>
              </div>
              <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Task Management</h3>
              <p className="text-gray-400 text-xs sm:text-sm">Organize your weekly tasks</p>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/projects')}
              className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:bg-white/10 transition-all group sm:col-span-2 lg:col-span-1"
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <DocumentDuplicateIcon className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-400 flex-shrink-0" />
                <span className="text-xl sm:text-2xl font-bold text-white">0</span>
              </div>
              <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Active Projects</h3>
              <p className="text-gray-400 text-xs sm:text-sm">Manage current projects</p>
            </motion.button>
          </div>
        </motion.div>

        {/* Premium Features Quick Actions */}
        {isPremiumUser && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8 sm:mb-12"
          >
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center space-x-2">
                <SparklesIcon className="w-5 h-5 text-purple-400" />
                <span>Premium Tools</span>
              </h2>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + 0 * 0.05 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/business-analytics')}
                className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:scale-105 transition-all group"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform">
                  <ChartBarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2 text-sm sm:text-base">Business Analytics</h3>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">Track performance, analyze growth patterns, and get AI-powered business insights.</p>
                <div className="flex items-center mt-3 sm:mt-4 text-purple-400 group-hover:text-purple-300 transition-colors">
                  <span className="text-xs sm:text-sm font-medium">View Analytics</span>
                  <ChartBarIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
                </div>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + 1 * 0.05 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/branding')}
                className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:scale-105 transition-all group"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform">
                  <SwatchIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2 text-sm sm:text-base">Branding & Documents</h3>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">Customize your business branding and create professional document templates.</p>
                <div className="flex items-center mt-3 sm:mt-4 text-pink-400 group-hover:text-pink-300 transition-colors">
                  <span className="text-xs sm:text-sm font-medium">Customize Brand</span>
                  <SwatchIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
                </div>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + 2 * 0.05 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/project-templates')}
                className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:scale-105 transition-all group"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r from-indigo-500 to-blue-500 flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform">
                  <DocumentDuplicateIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2 text-sm sm:text-base">Project Templates</h3>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">Create and manage reusable project workflows to streamline your business.</p>
                <div className="flex items-center mt-3 sm:mt-4 text-indigo-400 group-hover:text-indigo-300 transition-colors">
                  <span className="text-xs sm:text-sm font-medium">Manage Templates</span>
                  <DocumentDuplicateIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
                </div>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + 3 * 0.05 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowAIAssistant(true)}
                className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:scale-105 transition-all group"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform">
                  <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2 text-sm sm:text-base">AI Assistant</h3>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">Get personalized business recommendations and automated workflow suggestions.</p>
                <div className="flex items-center mt-3 sm:mt-4 text-emerald-400 group-hover:text-emerald-300 transition-colors">
                  <span className="text-xs sm:text-sm font-medium">Get Insights</span>
                  <SparklesIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
                </div>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + 4 * 0.05 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/calendar')}
                className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:scale-105 transition-all group"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform">
                  <CogIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2 text-sm sm:text-base">Smart Scheduling</h3>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">Optimize your schedule with intelligent booking suggestions and availability management.</p>
                <div className="flex items-center mt-3 sm:mt-4 text-blue-400 group-hover:text-blue-300 transition-colors">
                  <span className="text-xs sm:text-sm font-medium">Schedule Smart</span>
                  <CogIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
                </div>
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8"
        >
          {/* Activity Feed */}
          <div className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6">Recent Activity</h3>
            <div className="space-y-3 sm:space-y-4">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.05 }}
                    className="flex items-center space-x-3 sm:space-x-4 p-2 sm:p-3 rounded-xl hover:bg-white/5 transition-colors"
                  >
                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm sm:text-base">{activity.action}</p>
                      <p className="text-gray-400 text-xs sm:text-sm">{activity.client}</p>
                    </div>
                    <span className="text-gray-500 text-xs flex-shrink-0">{activity.time}</span>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-6 sm:py-8">
                  <ClipboardDocumentListIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-500 mx-auto mb-3 sm:mb-4" />
                  <p className="text-gray-400 mb-3 sm:mb-4 text-sm sm:text-base">No recent activity</p>
                  <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">Your business activity will appear here as you create shoots, add clients, and manage projects.</p>
                </div>
              )}
            </div>
          </div>

          {/* Mini Calendar Widget */}
          <MiniCalendarWidget shoots={allShoots} loading={loading} />
        </motion.div>
      </main>

      {/* Stats Modal */}
      <StatsModal
        isOpen={statsModal.isOpen}
        onClose={() => setStatsModal({ isOpen: false, type: 'clients' })}
        type={statsModal.type}
        data={{
          clients: clients,
          shoots: statsModal.type === 'shoots' ? completedShoots : statsModal.type === 'upcoming' ? scheduledShoots : undefined
        }}
        onEditShoot={handleEditShoot}
        onDeleteShoot={handleDeleteShoot}
        onViewDetails={handleOpenShootDetails}
        revenueRefreshKey={revenueRefreshKey}
      />

      {/* Premium Features Showcase */}
      <PremiumFeaturesShowcase
        isOpen={showPremiumFeatures}
        onClose={() => setShowPremiumFeatures(false)}
      />

      {/* AI Business Assistant Modal */}
      <AIBusinessAssistant
        isOpen={showAIAssistant}
        onClose={() => setShowAIAssistant(false)}
      />

      {/* Shoot Details Modal */}
      <ShootDetailsModal
        isOpen={shootDetailsModal.isOpen}
        onClose={handleCloseShootDetails}
        shoot={shootDetailsModal.shoot}
        onSave={handleSaveShootDetails}
        loading={savingShootDetails}
      />

      {/* Footer */}
      <Footer />
    </div>
  )
}

export default DashboardPage 